import { useTranslation } from "react-i18next";
import { FacilityIcon, CategoryIcon, ManualIcon } from "@/components/icons";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * The one-line "what this facility contains" meta: subfacilities · categories · manuals, each a small icon + count.
 *
 * A segment is OMITTED entirely when its count is zero — a LEAF renders nothing (no more "No subfacilities"), and a
 * facility with no categories/manuals shows no category/manual segment (the whole component returns `null` when every
 * count is zero, so the row keeps just its name). The icon carries the meaning visually (Network = subfacilities,
 * Folder = categories, open book = manuals); each segment is a `role="img"` with a translated `aria-label` so a
 * screen reader announces the whole icon+count as one labelled unit (reliable, unlike an `aria-label` on a bare span),
 * and a hover TOOLTIP repeats the label for sighted mouse users — so the compact glyph+number stays scannable a11y-safe.
 *
 * The tooltip trigger is `excludeFromTabOrder` — these counts are decorative, not interactive, so they must never
 * become keyboard tab stops inside the roving-tabindex tree (the `role="img"` label carries the meaning instead).
 *
 * Shared by the tree card ({@link FacilityNodeRow}) and the detail-pane header so both read identically; it takes
 * primitive counts (not a wire/tree type) to keep the detail pane decoupled from those shapes.
 */
export function FacilityCountsMeta({
  subfacilities,
  categories,
  manuals,
  className,
  labelled = false,
}: {
  subfacilities: number;
  categories: number;
  manuals: number;
  className?: string;
  /**
   * Spell each count out as visible text ("3 Unterbereiche") instead of glyph + number + tooltip — the drill-down
   * redesign (2026-10-21). Plain text, deliberately no chip background: these are not controls.
   * Shrinks with the page (owner 2026-11-02): below `md` only icon + number, below `sm` nothing visible. The full label
   * stays in the DOM as `sr-only`, so a screen reader still gets it at every width.
   */
  labelled?: boolean;
}) {
  const { t } = useTranslation();
  const segments = [
    subfacilities > 0
      ? { key: "sub", Icon: FacilityIcon, count: subfacilities, labelKey: "counts.subfacilities" as const }
      : null,
    categories > 0
      ? { key: "cat", Icon: CategoryIcon, count: categories, labelKey: "counts.categories" as const }
      : null,
    manuals > 0
      ? { key: "man", Icon: ManualIcon, count: manuals, labelKey: "counts.manuals" as const }
      : null,
  ].filter((s): s is NonNullable<typeof s> => s !== null);

  if (segments.length === 0) return null;

  if (labelled) {
    return (
      <ul className={cn("text-muted-foreground m-0 flex list-none flex-wrap items-center gap-x-4 gap-y-1 p-0 text-[13px] leading-[18px] max-sm:sr-only", className)}>
        {segments.map(({ key, Icon, count, labelKey }) => (
          <li key={key} className="inline-flex items-center gap-1.5 tabular-nums">
            <Icon aria-hidden="true" className="size-3.5 shrink-0" />
            <span aria-hidden="true" className="md:hidden">
              {count}
            </span>
            <span className="max-md:sr-only">{t(labelKey, { count })}</span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={cn("text-muted-foreground flex items-center gap-2.5 text-xs", className)}>
      {segments.map(({ key, Icon, count, labelKey }) => {
        // i18next's own `_one`/`_other` selection, not `count === 1 ? one : many` — German agrees with English on
        // the boundary but plenty of languages do not, and the branch is what hid these three from the scanner.
        const label = t(labelKey, { count });
        return (
          <TooltipTrigger key={key} delay={300} excludeFromTabOrder>
            <span role="img" aria-label={label} className="inline-flex shrink-0 items-center gap-1">
              <Icon aria-hidden="true" className="size-3.5 shrink-0" />
              {count}
            </span>
            <Tooltip>{label}</Tooltip>
          </TooltipTrigger>
        );
      })}
    </div>
  );
}
